import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ShopClientsAPI, { OrderComment } from "../../api/ShopClientsAPI";
import FilesAPI from "../../api/FilesAPI";
import { toastService } from "../../services/ToastService";
import { PermissionFlags } from "../PermissionFlags/PermissionFlags";
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import SectionTitle from "../SectionTitle/SectionTitle";
import TextArea from "../TextArea/TextArea";
import { isValidText } from "../../shared/validation";
import ListContainer from "../ListContainer/ListContainer";
import CommentCard from "../CommentCard/CommentCard";
import FileUploadWrapper from "../FileUploadWrapper/FileUploadWrapper";

type Props = {
  orderId: number,
  isDisabled: boolean,
  onOrderChange?: (status: string) => void,
};

function commentAttachmentFileName(originalName: string): string {
  const uuid = typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID().slice(0, 8)
    : `c-${Date.now().toString(36)}`;
  return `comments-${uuid}-${originalName}`;
}

function OrderComments({ orderId = 0, isDisabled = false }: Props) {

  const { t } = useTranslation();

  const [comments, setComments] = useState<OrderComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [draftComment, setDraftComment] = useState<OrderComment | undefined>();
  const [draftAttachmentFiles, setDraftAttachmentFiles] = useState<File[]>([]);
  const [deletingCommentId, setDeletingCommentId] = useState<number | null>(null);

  const load = async () => {
    if (!!orderId) {
      setIsLoading(true);
      const storyResp = await ShopClientsAPI.getComments(orderId);
      setIsLoading(false);
      setComments(storyResp.data);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const addDraftAttachmentFiles = (files: File[]) => {
    if (files.length === 0) return;
    setDraftAttachmentFiles((prev) => [...prev, ...files]);
  };

  const removeDraftAttachment = (index: number) => {
    setDraftAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const saveComment = async () => {
    if (!orderId || !draftComment?.comment?.trim()) return;
    setIsLoading(true);
    try {
      let uploadedNames: string[] = [];
      if (draftAttachmentFiles.length > 0) {
        for (const file of draftAttachmentFiles) {
          const fileName = commentAttachmentFileName(file.name);
          const formData = new FormData();
          formData.append("file", file);
          await FilesAPI.upload("/v1/files/orders/uploadV2", fileName, formData);
          uploadedNames.push(fileName);
        }
      }
      await ShopClientsAPI.addComment(orderId, {
        comment: draftComment.comment.trim(),
        attachmentUrls: uploadedNames,
      });
      setDraftComment(undefined);
      setDraftAttachmentFiles([]);
    } catch (error) {
      toastService.showToast(t("orders.story.add_step.error") ?? "Failed to save comment", "error");
    } finally {
      await load();
      setIsLoading(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!orderId) return;
    setDeletingCommentId(commentId);
    try {
      await ShopClientsAPI.deleteComment(orderId, commentId);
      await load();
    } catch (error) {
      toastService.showToast(t("orders.story.add_step.error") ?? "Failed to delete comment", "error");
    } finally {
      setDeletingCommentId(null);
    }
  };

  return ( 
    <div className="flex-1 h-full space-y-1">
      <SectionTitle text={`${t("orders.external.add_form.section.comments")!} ${comments.length > 0 ? `(${comments.length})` : ''}`} />
      { (comments && comments.length > 0) 
        && <PermissionFlags requiredFlags={['order-story-read'] || []}>
          <div className="max-h-80 py-5 px-1 overflow-y-auto space-y-3">
            { comments.map((c, index) => (
              <CommentCard
                key={c.id ?? index}
                comment={c}
                orderId={orderId}
                onDelete={deletingCommentId != null ? undefined : handleDeleteComment}
              />
            ))}
          </div>
        </PermissionFlags>
      }
      <PermissionFlags requiredFlags={['order-story-write'] || []}>
        { isLoading ? 
          <ListContainer isLoading={isLoading} toElementFn={() => <></>}/> 
          : 
          <>
            <TextArea
                validationFn={value => !!value && isValidText(value, 0, 1000)}
                label={t("orders.external.add_form.comment")!}
                errorMessage={"errors.too_long_short"}
                onChange={e => setDraftComment(prev => ({
                  ...prev,
                  comment: e.target.value,
                }))}
                isDisabled={isDisabled}
                maxLength={1000}
            />
            {orderId > 0 && (
              <div className="mt-2 space-y-1">
                <FileUploadWrapper
                  label="orders.orders_basket.add_form.files.button"
                  onChange={addDraftAttachmentFiles}
                  isDisabled={isDisabled}
                  allowedMultipleFiles
                />
                {draftAttachmentFiles.length > 0 && (
                  <ul className="text-sm text-gray-600 list-disc list-inside">
                    {draftAttachmentFiles.map((file, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <span className="truncate">{file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeDraftAttachment(idx)}
                          className="text-red-600 hover:underline"
                          aria-label="Remove attachment"
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <PrimaryButton text={t("global.add")!} isDisabled={isDisabled || !draftComment || !draftComment.comment?.trim()} fn={saveComment} />
          </> 
      }
      </PermissionFlags>
    </div> 
  );
}

export default OrderComments;
